import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { subscription } from "src/components/ApolloConnection/ApolloProviderComponent";
import { subscribeListAppointmentBeep, subscribeListTimeslotBeep } from "src/components/graphql/GraphQLsubscription";
import { fetchAppointments } from "src/features/appointments/appointmentsActions";
import { fetchTimeSlots } from "src/features/timeslots/timeslotsActions";

export const useDoctorSubscriptions = (doctorId) => {
    const dispatch = useDispatch();

    // Track latest appointment count to always fetch enough records after a beep.
    // We use a ref so the subscription callback always reads the freshest value
    // without needing to re-subscribe every time apptCount changes.
    const apptCount = useSelector((state) => state.appointments?.appointmentsList?.totalCount || 0);
    const apptCountRef = React.useRef(10);

    React.useEffect(() => {
        // +1 to account for the appointment that just triggered the beep
        apptCountRef.current = Math.max(apptCount + 1, 10);
    }, [apptCount]);

    React.useEffect(() => {
        if (!doctorId) return;

        // --- Subscription 1: appointment beep → fetch appointments ONLY ---
        const aptSubPromise = subscription(
            subscribeListAppointmentBeep(doctorId),
            {},
            async (response) => {
                const aptBeep = response?.data?.subscribeListAppointmentBeep;
                if (
                    !(aptBeep?.statusCode >= 200 && aptBeep?.statusCode < 300) ||
                    aptBeep?.errors != null
                ) return;

                try {
                    dispatch(fetchAppointments(apptCountRef.current, true));
                } catch (err) {
                    console.error("useDoctorSubscriptions: appointment beep error:", err);
                }
            },
            (error) => console.error("useDoctorSubscriptions: subscribeListAppointmentBeep error:", error)
        );

        // --- Subscription 2: timeslot beep → fetch timeslots ONLY ---
        const tsSubPromise = subscription(
            subscribeListTimeslotBeep,
            { doctorId },
            async (response) => {
                const beep = response?.data?.subscribeListTimeslotBeep;
                if (
                    !(beep?.statusCode >= 200 && beep?.statusCode < 300) ||
                    beep?.errors != null
                ) return;

                try {
                    dispatch(fetchTimeSlots(true));
                } catch (err) {
                    console.error("useDoctorSubscriptions: timeslot beep error:", err);
                }
            },
            (error) => console.error("useDoctorSubscriptions: subscribeListTimeslotBeep error:", error)
        );

        // Cleanup: unsubscribe both when doctorId changes or component unmounts
        return () => {
            aptSubPromise.then(sub => sub && sub.unsubscribe());
            tsSubPromise.then(sub => sub && sub.unsubscribe());
        };
    }, [doctorId]); // re-subscribe only if doctorId changes (e.g. first load when null → resolved)
};
